// frontend/src/hooks/useRecorder.js
import { useState, useRef, useContext } from 'react';
import { API_BASE_URL } from '../api/config';
import { AppContext } from '../contexts/AppContext';

/**
 * Custom hook for managing audio recording.
 * Provides functions to start, stop, and upload recordings,
 * along with the current recording status.
 */
export default function useRecorder() {
    const [isRecording, setIsRecording] = useState(false); // State to track if recording is active
    const mediaRecorder = useRef(null); // Ref to hold the current MediaRecorder instance
    const streamRef = useRef(null); // Ref to hold the current MediaStream for proper track stopping
    const { dbType } = useContext(AppContext);

    /**
     * Starts the audio recording process.
     * Requests microphone access, initializes MediaRecorder,
     * sets up event handlers for data collection and stopping,
     * and begins recording.
     *
     * Recorder, stream e chunk sono LOCALI a ogni "take": così, se una nuova
     * registrazione parte prima che la precedente abbia finito di chiudersi
     * (es. replay premuto durante la registrazione), le due non si calpestano —
     * niente blob vuoti né stream chiuso per sbaglio.
     */
    const startRecording = async () => {
        // Crucial: Prevent starting if MediaRecorder is already active,
        // even if `isRecording` state might be momentarily out of sync.
        if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
            // Update React state to reflect actual MediaRecorder state if it's out of sync
            if (!isRecording) setIsRecording(true);
            return;
        }

        try {
            // Request access to the user's microphone.
            // Disattiviamo le elaborazioni di default del browser (noise
            // suppression, AGC, echo cancellation) per catturare l'audio GREZZO:
            // niente alterazioni della voce e nessuna frammentazione/pumping
            // nei primi secondi (l'AGC/noise gate impiegano tempo a "convergere").
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                    channelCount: 1,
                },
            });

            // Tutto ciò che riguarda QUESTA take è catturato in variabili locali
            // (recorder, chunks, stream), così l'handler onstop opera solo sulla
            // propria registrazione anche se nel frattempo ne è partita un'altra.
            const recorder = new MediaRecorder(stream);
            const chunks = [];
            mediaRecorder.current = recorder;
            streamRef.current = stream;

            // Event handler for when audio data becomes available
            // This event fires periodically as the recorder collects data
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            // Event handler for when the recording stops:
            // finalizza il blob di questa take, lo carica e rilascia il microfono.
            recorder.onstop = () => {
                // Create a Blob from all collected audio chunks
                const blob = new Blob(chunks, { type: 'audio/webm' });
                // Call the upload function with the created blob
                uploadRecording(blob);

                // Stop all tracks of THIS take's stream to release the microphone.
                stream.getTracks().forEach(track => track.stop());

                // Azzera i ref SOLO se puntano ancora a questa take: se nel
                // frattempo è già partita una nuova registrazione (replay), non
                // dobbiamo toccarla.
                if (mediaRecorder.current === recorder) mediaRecorder.current = null;
                if (streamRef.current === stream) streamRef.current = null;
            };

            // Start the recording
            recorder.start();
            setIsRecording(true); // Update state to reflect recording is active immediately
        } catch (err) {
            console.error('Microphone access error:', err);
            setIsRecording(false); // Ensure state is reset if there's an error
            // Using alert as per guidelines; consider a custom modal in a full application.
            alert('Failed to access microphone. Please check permissions and try again.');
        }
    };

    /**
     * Stops the audio recording process.
     * If MediaRecorder is active, it calls its stop method,
     * and updates the recording state immediately.
     */
    const stopRecording = () => {
        // Prevent stopping if not currently recording or MediaRecorder is inactive
        if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
            // Ensure React state is false if MediaRecorder isn't active
            if (isRecording) setIsRecording(false);
            return;
        }

        mediaRecorder.current.stop(); // This triggers the onstop event handler
        setIsRecording(false); // Set isRecording to false IMMEDIATELY when stop is called
    };

    /**
     * Uploads the recorded audio Blob to the backend server.
     * @param {Blob} blob - The recorded audio data as a Blob.
     */
    const uploadRecording = async (blob) => {
        // Create FormData to send the audio file along with metadata
        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm'); // 'recording.webm' is the filename

        // Get exercise-specific metadata from localStorage
        formData.append('activityName', localStorage.getItem('exerciseId') || 'default');
        formData.append('userId', localStorage.getItem('userId') || '0');
        formData.append('levelNumber', localStorage.getItem('level') || '1');
        formData.append('exerciseNumber', localStorage.getItem('excerciseNumber') || '1');

        console.log('Uploading recording with level:', localStorage.getItem('level'));

        try {
            // Send the recording to the specified endpoint
            const response = await fetch(`${API_BASE_URL}/recordings`, {
                method: 'POST',
                headers: {
                    'x-database-type': dbType
                },
                body: formData,
                // Do not set 'Content-Type' header for FormData, browser sets it automatically
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Upload failed with status: ${response.status}, message: ${errorText}`);
            }

            const result = await response.json();
            console.log('Recording uploaded successfully:', result);
        } catch (err) {
            console.error('Upload failed:', err);
            // Handle upload failure, e.g., show an error message to the user
            alert('Recording upload failed. Please try again.');
        }
    };

    // Return the recording status and functions for parent components to use
    return {
        isRecording,
        startRecording,
        stopRecording,
    };
}
